/**
 * SkillLock-Integration (Spec 10) — Schemas fuer Konfiguration, verknuepfte
 * Tasks, den Status-Rueckkanal und die Wiki-Live-Suche (Spec 9.4).
 *
 * **Kein Status-Enum.** SkillLock-Status sind je Mandant frei definierte
 * Kanban-Spalten (`pm_statuses`); TicketHub spiegelt ausschliesslich das
 * Objekt `{id, name, is_done}`. Der Name ist Anzeige, die Entscheidung
 * („Task erledigt") haengt allein an `is_done`. Ein Enum hier waere ein Bug,
 * der erst beim ersten Kunden mit eigenen Spalten auffiele.
 *
 * Der Status-Webhook wird mit demselben HMAC-Verfahren geprueft wie unsere
 * ausgehenden Webhooks (`webhook-signature.ts`, Spec 11: „HMAC fuer alle
 * Webhooks in beide Richtungen"), inklusive Zeitstempel-Replayschutz. Dedup
 * laeuft ueber `event_id`, Out-of-order-Schutz ueber `changed_at`.
 *
 * Vollstaendige Erwartung an die Gegenseite: `docs/anforderungen-skilllock-api.md`.
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
/** Der gespiegelte Status — genau die drei Felder aus Spec 10. */
export const skilllockStatusSchema = z.object({
    id: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    is_done: z.boolean(),
});
/** Verknuepfung Ticket ↔ SkillLock-Task (`skilllock_task_links`, Spec 3.2). */
export const skilllockTaskLinkSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    ticket_id: uuidSchema,
    skilllock_task_id: z.string(),
    title: z.string(),
    url: z.string().nullable(),
    status_id: z.string().nullable(),
    status_name: z.string().nullable(),
    status_is_done: z.boolean(),
    /** `true` nach `task.deleted` — die Zeile bleibt als Historie stehen. */
    deleted: z.boolean(),
    last_synced_at: z.iso.datetime().nullable(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
// ────────────────────────────────── Konfiguration je Mandant (Spec 10) ──────
/**
 * Was das Admin-UI sieht. Der Service-Token und das Webhook-Secret sind
 * **write-only** (Spec 11) — heraus kommt nur, *ob* sie hinterlegt sind.
 */
/**
 * **Seit ADR 0017 zeigt der Mandant nur noch auf eine Verbindung.** Basis-URL,
 * Service-Token, Webhook-Secret und Standard-Board stehen in einer
 * `skilllock`-Verbindung aus dem Vault; `base_url` und `default_board_id` sind
 * hier abgeleitete Anzeige.
 *
 * `enabled` ist damit kein eigener Schalter mehr, sondern die Aussage „eine
 * Verbindung ist gewaehlt" — ein Zustand „eingeschaltet, aber ohne Zugang"
 * kann nicht mehr entstehen.
 */
export const skilllockSettingsSchema = z.object({
    enabled: z.boolean(),
    connection_id: uuidSchema.nullable(),
    connection_name: z.string().nullable(),
    base_url: z.string(),
    default_board_id: z.string().nullable(),
});
export const updateSkilllockSettingsSchema = z.object({
    /** `null` schaltet die Anbindung fuer diesen Mandanten ab. */
    connection_id: uuidSchema.nullable(),
});
// ─────────────────────────────────────── Task anlegen (Spec 10, Weg hin) ────
export const createSkilllockTaskSchema = z.object({
    /** Leer = Betreff des Tickets. */
    title: z.string().min(1).max(300).optional(),
    description: z.string().max(5000).optional(),
    /** Leer = `default_board_id` aus den Mandanten-Einstellungen. */
    board_id: z.string().max(200).nullable().default(null),
});
/** Antwort von `POST /tasks` der SkillLock-API. */
export const skilllockTaskResponseSchema = z.object({
    id: z.string().min(1).max(200),
    title: z.string().default(''),
    url: z.string().nullable().default(null),
    status: skilllockStatusSchema.nullable().default(null),
});
// ────────────────────────────────── Status-Webhook (Spec 10, Rueckkanal) ────
/**
 * SkillLock benennt als Absender seinen eigenen Signatur-Header (Konvention
 * wie `Stripe-Signature`/`X-Hub-Signature`). Format und Formel sind identisch
 * mit unserem ausgehenden `X-TicketHub-Signature` (`t=…,v1=…`, HMAC ueber
 * `<t>.<body>`) — nur der Name unterscheidet sich je Absender.
 */
export const SKILLLOCK_SIGNATURE_HEADER = 'x-skilllock-signature';
export const SKILLLOCK_WEBHOOK_EVENTS = ['task.status_changed', 'task.deleted'];
export const skilllockWebhookEventSchema = z.enum(SKILLLOCK_WEBHOOK_EVENTS);
/**
 * Der Umschlag, den SkillLock schickt. `event_id` ist der Dedup-Schluessel
 * (unique in `skilllock_webhook_events`), `changed_at` der Out-of-order-Schutz:
 * ein Ereignis, das aelter ist als der zuletzt verarbeitete Stand des Tasks,
 * wird verworfen.
 */
export const skilllockWebhookPayloadSchema = z.object({
    event_id: z.string().min(1).max(200),
    event: skilllockWebhookEventSchema,
    task_id: z.string().min(1).max(200),
    changed_at: z.iso.datetime(),
    /** Fehlt bei `task.deleted`. */
    status: skilllockStatusSchema.nullish().default(null),
    title: z.string().max(300).nullish().default(null),
});
/**
 * Der „Test-Event senden"-Knopf in SkillLock (Analogie: GitHubs `ping`).
 * Bezieht sich auf keinen Task — der Empfaenger verifiziert die Signatur,
 * antwortet 2xx und verwirft den Inhalt. Zusatzfelder sind erlaubt, damit
 * die Gegenseite den Ping erweitern kann, ohne uns zu brechen.
 */
export const skilllockPingPayloadSchema = z
    .object({
    event_id: z.string().min(1).max(200),
    event: z.literal('test.ping'),
    message: z.string().max(1000).nullish().default(null),
})
    .loose();
/** Antwort des Endpunkts — bewusst maschinenlesbar fuer die Gegenseite. */
export const skilllockWebhookResultSchema = z.object({
    received: z.literal(true),
    /** `true`, wenn dieses `event_id` schon einmal ankam. */
    duplicate: z.boolean(),
    /** `false`, wenn das Ereignis aelter war als der bekannte Stand. */
    applied: z.boolean(),
});
// ───────────────────────────────────────── Wiki-Live-Suche (Spec 9.4) ───────
/** Wie viele Treffer hoechstens in den Entwurfs-Kontext wandern. */
export const SKILLLOCK_WIKI_TOP_K = 3;
export const skilllockWikiHitSchema = z.object({
    id: z.string().min(1).max(200),
    title: z.string(),
    /** Volltext bzw. Auszug — die Grundlage der Zitate am Entwurf. */
    excerpt: z.string(),
    url: z.string().nullable().default(null),
    score: z.number().nullable().default(null),
});
export const skilllockWikiSearchResultSchema = z.object({
    /** Die tatsaechlich gestellte Anfrage (Stichwoerter, s. Spec 9.4). */
    query: z.string(),
    items: z.array(skilllockWikiHitSchema),
});
/**
 * Query-Bildung nach Spec 9.4: 5–8 Stichwoerter, nichts unter drei Zeichen
 * (faellt aus dem MySQL-Volltextindex), keine deutschen Fuellwoerter (die
 * Stoppwortliste des Index ist englisch). Reine Funktion, damit sie ohne
 * Netzwerk testbar ist und im UI dieselbe Vorschau erzeugt.
 */
const GERMAN_STOPWORDS = new Set([
    'aber', 'alle', 'allem', 'allen', 'aller', 'alles', 'also', 'auch', 'auf', 'aus', 'bei',
    'beim', 'bin', 'bis', 'bist', 'dass', 'dein', 'deine', 'dem', 'den', 'der', 'des', 'dessen',
    'die', 'dies', 'diese', 'diesem', 'diesen', 'dieser', 'dieses', 'doch', 'dort', 'durch',
    'ein', 'eine', 'einem', 'einen', 'einer', 'eines', 'einfach', 'etwa', 'euer', 'eure', 'fuer',
    'für', 'ganz', 'gar', 'gegen', 'gibt', 'habe', 'haben', 'hallo', 'hat', 'hatte', 'hier',
    'ich', 'ihm', 'ihn', 'ihnen', 'ihr', 'ihre', 'immer', 'ist', 'kann', 'kein', 'keine',
    'koennen', 'können', 'mehr', 'mein', 'meine', 'mich', 'mir', 'mit', 'nach', 'nicht', 'noch',
    'nun', 'nur', 'oder', 'ohne', 'schon', 'sehr', 'sein', 'seine', 'sich', 'sie', 'sind',
    'soll', 'sollen', 'sonst', 'ueber', 'über', 'und', 'uns', 'unser', 'unsere', 'vom', 'von',
    'vor', 'war', 'waren', 'was', 'wenn', 'werden', 'wie', 'wieder', 'wir', 'wird', 'wo',
    'wurde', 'wurden', 'zum', 'zur', 'zwar',
]);
export const SKILLLOCK_WIKI_QUERY_MIN_WORD_LENGTH = 3;
export const SKILLLOCK_WIKI_QUERY_MAX_WORDS = 8;
export function buildWikiSearchQuery(...parts) {
    const seen = new Set();
    const words = [];
    for (const part of parts) {
        if (!part)
            continue;
        for (const raw of part.toLowerCase().split(/[^\p{L}\p{N}]+/u)) {
            if (raw.length < SKILLLOCK_WIKI_QUERY_MIN_WORD_LENGTH)
                continue;
            if (GERMAN_STOPWORDS.has(raw))
                continue;
            if (seen.has(raw))
                continue;
            seen.add(raw);
            words.push(raw);
            if (words.length >= SKILLLOCK_WIKI_QUERY_MAX_WORDS)
                return words.join(' ');
        }
    }
    return words.join(' ');
}
//# sourceMappingURL=skilllock.js.map